import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { DocumentPdfViewerModal } from '../components/documents/DocumentPdfViewerModal';
import { DocumentsDeleteModal } from '../components/documents/DocumentsDeleteModal';
import { DocumentsFiltersBar } from '../components/documents/DocumentsFiltersBar';
import { DocumentsKnowledgeBaseSidebar } from '../components/documents/DocumentsKnowledgeBaseSidebar';
import { DocumentsList } from '../components/documents/DocumentsList';
import { DocumentsLoadingState } from '../components/documents/DocumentsLoadingState';
import { useDocumentsPageData } from '../hooks/useDocumentsPageData';

function DocumentsPageHeader({ onBack }) {
  return (
    <div className="border-b border-border">
      <div className="mx-auto max-w-6xl p-6">
        <button
          type="button"
          data-testid="documents-back-to-chat"
          onClick={onBack}
          className="mb-4 flex items-center gap-2 text-muted-foreground transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Retour au chat</span>
        </button>
        <h1 className="text-3xl font-serif tracking-tight">Gestion des documents</h1>
      </div>
    </div>
  );
}

function DocumentsPageEmptyState({ message }) {
  return <div data-testid="documents-empty-state" className="py-12 text-center text-muted-foreground">{message}</div>;
}

function DocumentsMainPanel({ documentsPageData, onViewClick }) {
  return (
    <div className="flex-1">
      <DocumentsFiltersBar
        documents={documentsPageData.documents}
        filteredDocuments={documentsPageData.filteredDocuments}
        searchQuery={documentsPageData.searchQuery}
        setSearchQuery={documentsPageData.setSearchQuery}
        typeFilter={documentsPageData.typeFilter}
        setTypeFilter={documentsPageData.setTypeFilter}
        dateFilter={documentsPageData.dateFilter}
        setDateFilter={documentsPageData.setDateFilter}
      />
      {documentsPageData.filteredDocuments.length === 0 ? (
        <DocumentsPageEmptyState message={documentsPageData.emptyStateMessage} />
      ) : (
        <DocumentsList
          documents={documentsPageData.filteredDocuments}
          deleting={documentsPageData.deleting}
          onDeleteClick={documentsPageData.setDeleteModal}
          onViewClick={onViewClick}
        />
      )}
    </div>
  );
}

function DocumentsPageContent({ documentsPageData, onViewClick }) {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex gap-6">
        <DocumentsKnowledgeBaseSidebar
          knowledgeBases={documentsPageData.knowledgeBases}
          selectedKB={documentsPageData.selectedKB}
          onSelect={documentsPageData.setSelectedKB}
        />
        <DocumentsMainPanel documentsPageData={documentsPageData} onViewClick={onViewClick} />
      </div>
    </div>
  );
}

function DocumentsPageShell({ documentsPageData, onBack, viewingDocument, onOpenViewer, onCloseViewer }) {
  return (
    <div className="min-h-screen bg-background">
      <DocumentsDeleteModal
        document={documentsPageData.deleteModal}
        onClose={() => documentsPageData.setDeleteModal(null)}
        onConfirm={documentsPageData.handleDeleteConfirm}
      />
      {viewingDocument ? (
        <DocumentPdfViewerModal
          documentId={viewingDocument.id}
          title={viewingDocument.title}
          onClose={onCloseViewer}
        />
      ) : null}
      <DocumentsPageHeader onBack={onBack} />
      <DocumentsPageContent documentsPageData={documentsPageData} onViewClick={onOpenViewer} />
    </div>
  );
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const documentsPageData = useDocumentsPageData();
  const [viewingDocument, setViewingDocument] = useState(null);
  const handleBack = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);
  const handleCloseViewer = useCallback(() => setViewingDocument(null), []);

  if (documentsPageData.loading) {
    return <DocumentsLoadingState />;
  }

  return (
    <DocumentsPageShell
      documentsPageData={documentsPageData}
      onBack={handleBack}
      viewingDocument={viewingDocument}
      onOpenViewer={setViewingDocument}
      onCloseViewer={handleCloseViewer}
    />
  );
}
